import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Main } from '../../components'
import { useQuery } from '@tanstack/react-query'
import { getCharacter, getCharacterByPath } from '../../api/character'
import CharacterHeader from './components/CharacterHeader'
import GearTable from './components/GearTable'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

import DashboardTile from '../../components/DashboardTile'
import GearSummary from './components/GearSummary'
import UpgradesSummary from './components/UpgradesSummary'
import VaultSummary from './components/VaultSummary'
import TrackingSummary from './components/TrackingSummary'
import { useBlizzardApi } from '../../helpers'
import useReferenceStore from '../../state/useReferenceStore'
import useBasePath from '../../hooks/useBasePath'
import { Skeleton } from '@/components/ui/skeleton'
import usePrefetchedStore from '../../state/usePrefetchedStore'
import ActivityTimeline from '../TeamDashboard/subpages/TeamApplicants/components/ActivityTimeline'

const CharacterDashboard = () => {
  const baseCharacterPath = useBasePath('character')

  const { t } = useTranslation()
  const {
    uriRegion,
    uriRealm,
    uriName,
    blizzardInfo,
    blizzardStatus,
    searchBlizzard,
  } = useBlizzardApi<wowaudit.CharacterReference>('character', t)
  const { getReference, storeReferences } = useReferenceStore()
  const { getData } = usePrefetchedStore()
  const characterReference =
    blizzardInfo ?? getReference<wowaudit.CharacterReference>(baseCharacterPath)

  const queryFn = async () => {
    const response = characterReference
      ? await getCharacter(characterReference.id)
      : await getCharacterByPath(baseCharacterPath as string)

    if (response) {
      storeReferences([
        ...response.teamReferences,
        ...(response.guildReference ? [response.guildReference] : []),
      ])
    }

    return response
  }

  const {
    data: character,
    refetch,
    isPending,
  } = useQuery({
    queryKey: ['character', { path: baseCharacterPath }],
    queryFn,
    initialData: getData<wowaudit.Character>(baseCharacterPath),
    staleTime: 1000 * 60 * 60,
  })

  return (
    <Main>
      <CharacterHeader
        character={character}
        characterReference={characterReference}
        uriRegion={uriRegion}
        uriRealm={uriRealm}
        uriName={uriName}
        isPending={isPending}
      />

      {isPending && !characterReference ? (
        <div>
          <div className='mt-8 flex items-center gap-2'>
            <Skeleton className='h-[15rem] grow bg-muted rounded-md' />
            <Skeleton className='h-[15rem] grow bg-muted rounded-md' />
            <Skeleton className='h-[15rem] grow bg-muted rounded-md' />
            <Skeleton className='h-[15rem] grow bg-muted rounded-md' />
          </div>

          <Skeleton className='mt-8 h-[40rem] bg-muted rounded-md' />
        </div>
      ) : (
        <>
          {!(character ?? characterReference)?.id ? (
            <div className='mt-8'>
              <Alert variant='warning'>
                <AlertTitle>{t('dashboard.character.not-found')}</AlertTitle>
                <AlertDescription className='mt-4'>
                  <p>{t('dashboard.character.not-found-description')}</p>
                  {blizzardStatus === 'not-found' ? (
                    <p>
                      {t(
                        'dashboard.character.not-found-official-api-description',
                      )}
                    </p>
                  ) : (
                    <Button
                      className='mt-2 text-foreground'
                      variant='outline'
                      loading={blizzardStatus === 'pending'}
                      spinner='blizzard'
                      onClick={searchBlizzard}
                    >
                      {t(`header.search-official-api`)}
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            </div>
          ) : null}

          {characterReference &&
          (character ?? characterReference).gdprStatus !== 'exists' ? (
            <div className='mt-8'>
              <Alert variant='warning'>
                <AlertTitle>
                  {t('dashboard.character.no-longer-exists')}
                </AlertTitle>
                <AlertDescription className='mt-4'>
                  <p>
                    <Trans
                      i18nKey='dashboard.character.no-longer-exists-description'
                      components={[
                        <Link
                          key='profile'
                          to='/profile/characters'
                          className='text-primary'
                        />,
                      ]}
                    />
                  </p>
                </AlertDescription>
              </Alert>
            </div>
          ) : null}

          <>
            <div className='mt-8 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2'>
              <DashboardTile
                title='Gear summary'
                size='medium'
                dataPresent={!!character?.details}
              >
                <GearSummary character={character} />
              </DashboardTile>

              <DashboardTile
                title='Item upgrades'
                size='medium'
                dataPresent={!!character?.details}
              >
                <UpgradesSummary character={character} />
              </DashboardTile>

              <DashboardTile
                title='Great vault (this week)'
                size='medium'
                dataPresent={!!character?.details}
              >
                <VaultSummary character={character} />
              </DashboardTile>

              <DashboardTile
                title='Teams'
                size='medium'
                dataPresent={!!character?.details}
              >
                <TrackingSummary character={character} refetch={refetch} />
              </DashboardTile>
            </div>

            {character?.details?.timeline && (
              <DashboardTile
                title='Raid history'
                size='medium'
                dataPresent={!!character?.details}
              >
                <ActivityTimeline timeline={character?.details?.timeline} />
              </DashboardTile>
            )}

            <div className='my-4'>
              <GearTable character={character} />
            </div>
          </>
        </>
      )}
    </Main>
  )
}

export default CharacterDashboard
